import React from 'react';
import { motion } from 'framer-motion';
import { AccountValidationInput } from './AccountValidationInput';
import { ErrorMessage } from './ErrorMessage';
import { LoadingButton } from './LoadingButton';

interface InitialSetupViewProps {
  accountIdentifier: string;
  onAccountIdentifierChange: (value: string) => void;
  loading: boolean;
  error: string | null;
  isAccountValid: boolean;
  onNext: () => void;
}

/**
 * InitialSetupView Component
 *
 * Displays the initial account identifier input form for managed key authentication.
 *
 * @component
 * @example
 * ```tsx
 * <InitialSetupView
 *   accountIdentifier="myorg-myaccount"
 *   onAccountIdentifierChange={setAccountIdentifier}
 *   loading={false}
 *   error={null}
 *   isAccountValid={true}
 *   onNext={handleNext}
 * />
 * ```
 */
export const InitialSetupView: React.FC<InitialSetupViewProps> = ({
  accountIdentifier,
  onAccountIdentifierChange,
  loading,
  error,
  isAccountValid,
  onNext,
}) => {
  return (
    <motion.div className="space-y-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div>
        <h3 className="text-lg text-dataops-dark">Let&apos;s Setup Managed Key Authentication</h3>
        <p className="mt-2 text-sm text-secondary-text">
          Let DataOps.live generate and securely manage your authentication keys.
        </p>
      </div>

      {error !== null && <ErrorMessage error={error} accountIdentifier={accountIdentifier} />}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <AccountValidationInput
          value={accountIdentifier}
          onChange={onAccountIdentifierChange}
          disabled={loading}
          required
        />
        <p className="mt-2 text-xs text-secondary-text">
          Enter your Snowflake account identifier (e.g., myorg-myaccount). You can find this in the URL of your account:{' '}
          <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">
            &lt;account_identifier&gt;.snowflakecomputing.com
          </code>
        </p>
      </motion.div>

      <div className="flex justify-end pt-6">
        <LoadingButton
          onClick={onNext}
          disabled={loading || !isAccountValid}
          loading={loading}
          loadingText="Creating Account..."
          ariaLabel={loading ? 'Creating account' : 'Proceed to setup'}
        >
          Next
        </LoadingButton>
      </div>
    </motion.div>
  );
};
